/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */

import {ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, Optional} from '@angular/core';
import {DomSanitizer} from '@angular/platform-browser';

import {EMPTY, Observable} from 'rxjs';
import {catchError, delay, finalize, switchMap, takeUntil, tap} from 'rxjs/operators';

import {ProfileMode} from '../../../../app.constants';
import {FileModel, getFileNameAndExtension, Preview} from '../../../../shared/utils/files';
import {EmpFile} from '../../../employees/employees.model';
import {EmployeesService} from '../../../employees/employees.service';
import {FileChangesSessionService} from '../../../files/file-changes-session.service';
import {FileChangesService} from '../../../files/file-changes.service';
import {AbstractField} from '../abstract-field';

/**
 * Field for uploading files
 */
@Component({
  selector: 'gp-form-field-file',
  templateUrl: 'file-field.component.html',
  styleUrls: ['file-field.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FileFieldComponent extends AbstractField implements OnInit {

  public isOver: boolean = false;
  public maxFileSize: boolean = false;
  public savedMsg: boolean = false;
  public loading: boolean = false;
  public downloading: boolean = false;
  public file: EmpFile;
  public profileMode: ProfileMode = ProfileMode.CLASSIC;

  public showViewDocumentPanelInFileField = false;
  public currentFileName: string;
  public reportDefinitionPreview: Preview;
  public previewError: boolean;
  public loadingPreview: boolean;
  public noSupportedFormat: boolean;
  public supportedFileExtensions = ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'bmp'];
  public isViewAttachmentsActive = false;

  constructor(private cd: ChangeDetectorRef,
              private employeesService: EmployeesService,
              private fileChangesService: FileChangesService,
              private sanitizer: DomSanitizer,
              @Optional() private fileChanges: FileChangesSessionService) {
    super();
  }

  public ngOnInit(): void {
    this.init();
  }

  public init() {
    this.employeesService.empFiles$
      .pipe(
        tap((files: EmpFile[]) => {
          this.file = files.find(f => `${f.id}` === `${this.value.value}`);
          this.value.file = this.file;
          this.cd.markForCheck();
        }),
        takeUntil(this.componentDestroyed$)
      )
      .subscribe();

    this.employeesService.removeFile$
      .pipe(
        switchMap((file: EmpFile) => this.file === file ? this.deleteFileRequest() : EMPTY),
        takeUntil(this.componentDestroyed$)
      )
      .subscribe();
  }

  public uploadFile(event): void {
    this.maxFileSize = false;
    if (event.target.files.length) {
      const file = event.target.files[0];
      if (file.size > 5120000) {
        this.maxFileSize = true;
        event.target.value = null;
      } else {

        this.loading = true;
        this.employeesService.uploadFile(
          file,
          this.descriptor.metadatas.contractId,
          this.groupZoneDescriptor.transId,
          this.descriptor.transId,
          this.groupZoneDescriptor.groupZoneID,
          this.descriptor.metadatas.uploadFileUrl,
          this.descriptor.metadatas.fileUrlsAuthenticated,
          this.profileMode
        )
          .pipe(
            finalize(() => {
              event.target.value = null;
              this.loading = false;
              this.cd.markForCheck();
            }),
            tap((response: any) => {
              this.loading = false;
              this.savedMsg = true;
              event.target.value = null;
              this.value.value = response.body.id;
              this.control.setValue(response.body.id);
              this.control.markAsDirty();
              if (this.fileChanges?.enabled) {
                this.fileChangesService.markFileForUpload(
                  this.fileChanges, response.body.id,
                  this.descriptor.metadatas.deleteFileUrl,
                  this.descriptor.metadatas.fileUrlsAuthenticated,
                  this.profileMode);
              }
              this.cd.markForCheck();
            }),
            switchMap(() => this.employeesService.getEmpFiles(
              this.descriptor.metadatas.contractId,
              this.descriptor.metadatas.getFilesUrl,
              this.descriptor.metadatas.fileUrlsAuthenticated,
              this.profileMode)
            ),
            delay(3000),
            tap(() => {
              this.savedMsg = false;
              this.cd.markForCheck();
            }),
            catchError(() => EMPTY),
            takeUntil(this.componentDestroyed$)
          )
          .subscribe();
      }
    }
  }

  public onDragOver(): void {
    this.isOver = true;
  }

  public onDragLeave(): void {
    this.isOver = false;
  }

  public onDrop(): void {
    this.isOver = false;
  }

  public deleteFile(): void {
    this.deleteFileRequest().pipe(takeUntil(this.componentDestroyed$)).subscribe();
  }

  private deleteFileRequest(): Observable<any> {
    if (!this.file) {
      return EMPTY;
    }
    const {deleteFileUrl, fileUrlsAuthenticated} = this.descriptor.metadatas;
    const deletion = this.fileChanges?.enabled
      ? this.fileChangesService.markFileForDeletion(this.fileChanges, this.file.id, deleteFileUrl, fileUrlsAuthenticated, this.profileMode)
      : this.employeesService.removeFile(this.file.id, deleteFileUrl, fileUrlsAuthenticated, this.profileMode);
    return deletion.pipe(
      tap(() => {
        this.value.value = '';
        this.control.markAsDirty();
        this.control.setValue('');
        this.file = null;
        this.value.file = null;
        this.cd.markForCheck();
      }),
      catchError(() => EMPTY)
    );
  }

  public downloadFile(): void {
    this.downloading = true;
    this.cd.markForCheck();

    this.employeesService
      .downloadFile(this.file, this.descriptor.metadatas.downloadFileUrl, this.descriptor.metadatas.fileUrlsAuthenticated, this.profileMode)
      .pipe(
        finalize(() => {
          this.downloading = false;
          this.cd.markForCheck();
        })
      )
      .subscribe();
  }

  public viewFile(event): void {
    event.stopImmediatePropagation();

    if (!this.showViewDocumentPanelInFileField) {
      this.showViewDocumentPanelInFileField = true;
      this.previewDocument().subscribe();
    }
  }

  public isSupportedFileType(fileName: string): boolean {
    const fileExtension = getFileNameAndExtension(fileName).fileExtension.toLowerCase();
    return this.supportedFileExtensions.includes(fileExtension);
  }

  public onCloseSidePanel() {
    this.showViewDocumentPanelInFileField = false;
    this.reportDefinitionPreview = null;
    this.currentFileName = '';
    this.previewError = false;
  }

  private previewDocument() {
    this.loadingPreview = true;
    this.currentFileName = 'placeholder text for skeleton text';
    return this.employeesService
      .getFile(this.file, this.descriptor.metadatas.downloadFileUrl, this.descriptor.metadatas.fileUrlsAuthenticated, this.profileMode)
      .pipe(
        tap((fileModel: FileModel) => {
          if (fileModel) {
            this.currentFileName = getFileNameAndExtension(fileModel.fileName).fileName;
            if ((navigator as any).msSaveBlob) {
              (navigator as any).msSaveBlob(fileModel.blob, fileModel.fileName);
            } else {
              const supportedFileTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/bmp'];
              this.noSupportedFormat = !supportedFileTypes.includes(fileModel.blob?.type);
              this.reportDefinitionPreview = this.getDocumentPreview(URL.createObjectURL(fileModel.blob), fileModel.fileName);
              this.previewError = false;
            }
          } else {
            this.previewError = true;
            this.reportDefinitionPreview = null;
            this.currentFileName = '';
          }
        }),
        catchError(() => {
          this.previewError = true;
          this.reportDefinitionPreview = null;
          this.currentFileName = '';
          return EMPTY;
        }),
        finalize(() => {
          this.loadingPreview = false;
          this.cd.detectChanges();
        })
      );
  }

  private getDocumentPreview(href: string, fileName: string): Preview {
    return {
      iframe: this.sanitizer.bypassSecurityTrustHtml(`
             <iframe width="100%" height="100%" src="${href}"
                     name="${fileName}">
             </iframe>
          `), href: this.sanitizer.bypassSecurityTrustUrl(href), download: fileName
    };
  }
}
